import * as React from "react";
import styled from "@emotion/styled";
import Dropdown from "react-bootstrap/Dropdown";
import { EaselButton } from "../global/EaselButton";
import { DropdownIcon } from "../../icons/Dropdown";
import { ForMyClassroom } from "../../icons/ForMyClassroom";
import { ForMyStore } from "../../icons/ForMyStore";
import { designTokens, easelColors } from "../../helpers/constants";

interface ClickProps {
  href?: string;
  onClick?: () => void;
}

interface CreateItemProps {
  heading: string;
  subHeading?: string;
  onClick: ClickProps;
  pathToBetaImage?: string;
}

const CreateItem: React.FC<CreateItemProps> = ({
  heading,
  subHeading,
  onClick,
  pathToBetaImage,
}: CreateItemProps) => {
  return (
    <Item {...onClick} noSubheading={subHeading === undefined}>
      <Title>
        {heading}
        {pathToBetaImage !== undefined && (
          <Beta src={pathToBetaImage} alt="beta tag" />
        )}
      </Title>
      {subHeading && <Description>{subHeading}</Description>}
    </Item>
  );
};

const Title = styled.span({
  fontSize: "1rem",
  margin: "0",
  display: "flex",
});
const Description = styled.span((props: { sectionHeader?: boolean }) => ({
  display: "block",
  fontSize: "0.8125rem",
  fontWeight: 400,
  color: designTokens["ds-color-gray-base"],
  margin: "0",
  padding: props.sectionHeader ? "0 0.25rem" : "0",
}));

const Beta = styled.img({ margin: "0 0.5rem" });

export enum DropdownItem {
  TeachActivity,
  TeachAssessment,
  SellActivity,
  SellAssessment,
}

export type LinkMap = Record<DropdownItem, ClickProps>;

export interface CreateDropDownProps {
  linkMap: LinkMap;
  isSeller: boolean;
  isActivityListingsEnabled: boolean;
  pathToBetaImage?: string;
}

interface CreateButtonProps {
  onClick?: () => void;
}

const CreateButton: React.ForwardRefRenderFunction<
  HTMLDivElement,
  CreateButtonProps
> = ({ onClick }: CreateButtonProps, ref) => {
  return (
    <ButtonWrapper ref={ref} data-test-id="create-dropdown-toggle">
      <EaselButton
        onClick={onClick}
        variant="primary"
        buttonType="solid"
        size="xs"
        darkBackground
      >
        <ButtonContent>
          Create <DropdownIcon />
        </ButtonContent>
      </EaselButton>
    </ButtonWrapper>
  );
};

const ButtonContent = styled.span({
  whiteSpace: "nowrap",
  svg: {
    margin: "0 0.5rem",
  },
});

export const CreateToggle = React.forwardRef(CreateButton);

export const CreateMenu: React.FC<CreateDropDownProps> = (
  props: CreateDropDownProps
) => {
  const { linkMap, pathToBetaImage, isSeller, isActivityListingsEnabled } =
    props;

  const buyerAndSellerView = (
    <>
      <CreateItem
        heading="Activity"
        subHeading="Create an interactive lesson"
        onClick={linkMap[DropdownItem.TeachActivity]}
      />
      <CreateItem
        heading="Assessments"
        subHeading="Build an auto-graded quiz"
        onClick={linkMap[DropdownItem.TeachAssessment]}
        pathToBetaImage={pathToBetaImage}
      />
    </>
  );
  return (
    <Dropdown>
      <Dropdown.Toggle as={CreateToggle} id="create-toggle" />
      <Menu x-placement="bottom-start">
        {isSeller ? (
          <>
            <Description sectionHeader>
              <IconContainer>
                <ForMyClassroom />
              </IconContainer>
              FOR MY CLASSROOM
            </Description>
            {buyerAndSellerView}
            <Divider />
            <Description sectionHeader>
              <IconContainer>
                <ForMyStore />
              </IconContainer>
              FOR MY STORE
            </Description>
            {isActivityListingsEnabled && (
              <CreateItem
                heading="Activity for listing"
                onClick={linkMap[DropdownItem.SellActivity]}
              />
            )}
            <CreateItem
              heading="Assessment for listing"
              onClick={linkMap[DropdownItem.SellAssessment]}
            />
          </>
        ) : (
          buyerAndSellerView
        )}
      </Menu>
    </Dropdown>
  );
};

const Menu = styled(Dropdown.Menu)({
  borderTop: "1px solid white",
  color: designTokens["ds-color-gray-darkest"],
  boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.175)",
  minWidth: "18rem",
  a: {
    margin: "0",
  },
});

const Item = styled(Dropdown.Item, {
  shouldForwardProp: (prop) => prop !== "noSubheading",
})((props: { noSubheading?: boolean }) => ({
  background: "transparent",
  fontSize: "0.875rem",
  fontWeight: 500,
  color: designTokens["ds-color-gray-darkest"],
  "&:hover": {
    background: easelColors.easelPeriwinkle100,
    color: designTokens["ds-color-gray-darkest"],
  },
  padding: "0 48px",
  height: props.noSubheading ? "2.75rem" : "3.75rem",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",

  "&focus": {
    background: easelColors.easelPeriwinkle200,
  },
}));

const ButtonWrapper = styled.div({
  height: "100%",
  display: "flex",
  alignItems: "center",
});

const Divider = styled(Dropdown.Divider)({
  margin: "0.5rem 0",
});

const IconContainer = styled.span`
  margin: 0 13px;
`;
